import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Plus, Package, Copy } from "lucide-react";
import { useParcelTypes, ParcelType } from "@/hooks/useParcelTypes";
import { useOrganizationContext } from "@/context/organization-provider";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import ParcelTypeForm from "../parcel-types/ParcelTypeForm";
import ParcelTypesList from "../parcel-types/ParcelTypesList";

interface OrganizationParcelTypesManagerProps {
  organizationId?: string;
}

const OrganizationParcelTypesManager = ({ organizationId: explicitOrgId }: OrganizationParcelTypesManagerProps = {}) => {
  const { organization } = useOrganizationContext();
  
  // Use explicit org ID if provided (super admin context), otherwise use context org
  const targetOrgId = explicitOrgId || organization?.id;
  
  const { parcelTypes, loading, createParcelType, updateParcelType, batchUpdateSortOrder, deleteParcelType, refetch } = useParcelTypes(
    true, 
    false, 
    true, // orgSpecificOnly - only show org-specific types
    targetOrgId
  );
  
  // Early return if no organization is available
  if (!organization || !targetOrgId) {
    return (
      <div className="flex justify-center py-8">
        <div className="text-center">
          <div className="text-muted-foreground mb-2">No organization selected</div>
          <div className="text-sm text-muted-foreground">Please select an organization to manage parcel types</div>
        </div>
      </div>
    );
  }

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingType, setEditingType] = useState<ParcelType | null>(null);
  const [reordering, setReordering] = useState(false);
  const [copyingDefaults, setCopyingDefaults] = useState(false);

  const handleAddClick = () => {
    setEditingType(null);
    setDialogOpen(true);
  };

  const handleEdit = (parcelType: ParcelType) => {
    setEditingType(parcelType);
    setDialogOpen(true);
  };

  const handleFormSubmit = async (formData: any) => {
    try {
      if (editingType) {
        await updateParcelType(editingType.id, formData);
      } else {
        await createParcelType(formData);
      }
      setDialogOpen(false);
      setEditingType(null);
    } catch (error) {
      console.error('Error submitting parcel type form:', error);
      toast.error('Failed to save parcel type');
    }
  };

  const handleDelete = async (id: string) => {
    if (confirm('Are you sure you want to delete this parcel type?')) {
      try {
        await deleteParcelType(id);
      } catch (error) {
        console.error('Error deleting parcel type:', error);
        toast.error('Failed to delete parcel type');
      }
    }
  };

  const handleToggleEnabled = async (parcelType: ParcelType) => {
    try {
      await updateParcelType(parcelType.id, { is_enabled: !parcelType.is_enabled });
    } catch (error) {
      console.error('Error toggling parcel type:', error);
      toast.error('Failed to update parcel type');
    }
  };

  const handleDragEnd = async (result: any) => {
    if (!result.destination || reordering) return;

    setReordering(true);
    
    try {
      // Use only organization-specific parcel types for drag & drop
      const items = Array.from(orgParcelTypes);
      const [reorderedItem] = items.splice(result.source.index, 1);
      items.splice(result.destination.index, 0, reorderedItem);

      const batchUpdates = items.map((item, index) => ({
        id: item.id,
        sort_order: index + 1
      }));

      await batchUpdateSortOrder(batchUpdates);
    } catch (error) {
      console.error('Error reordering parcel types:', error);
      toast.error('Failed to reorder parcel types');
    } finally {
      setReordering(false);
    }
  };

  const copyDefaultParcelTypes = async () => {
    if (!targetOrgId) {
      toast.error('No organization selected');
      return;
    }

    if (copyingDefaults) {
      return; // Prevent double clicks
    }

    setCopyingDefaults(true);
    try {
      const { error } = await supabase.rpc('copy_default_parcel_types_to_org', {
        target_org_id: targetOrgId
      });

      if (error) {
        console.error('Error copying default parcel types:', error);
        toast.error('Failed to copy default parcel types');
        return;
      }

      toast.success('Default parcel types copied successfully');
      // Refetch to show the new parcel types
      await refetch();
    } catch (error) {
      console.error('Error copying default parcel types:', error);
      toast.error('Failed to copy default parcel types');
    } finally {
      setCopyingDefaults(false);
    }
  };

  // Filter to show only organization-specific parcel types
  const orgParcelTypes = parcelTypes.filter(pt => pt.org_id === targetOrgId);
  const hasOrgParcelTypes = orgParcelTypes.length > 0;

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center space-x-2">
                <Package className="w-5 h-5" />
                <span>Parcel Types</span>
              </CardTitle>
              <p className="text-sm text-muted-foreground mt-1">
                Manage parcel types for {organization.display_name || organization.name}
              </p>
            </div>
            <div className="flex space-x-2">
              {!hasOrgParcelTypes && (
                <Button
                  onClick={copyDefaultParcelTypes}
                  disabled={copyingDefaults || loading}
                  variant="outline"
                  className="flex items-center space-x-2"
                >
                  <Copy className="w-4 h-4" />
                  <span>{copyingDefaults ? 'Copying...' : 'Copy Defaults'}</span>
                </Button>
              )}
              <Button onClick={handleAddClick} disabled={loading} className="flex items-center space-x-2">
                <Plus className="w-4 h-4" />
                <span>Add Parcel Type</span>
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-8">
              <div className="flex items-center space-x-2">
                <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
                <span className="text-sm text-muted-foreground">Loading parcel types...</span>
              </div>
            </div>
          ) : !hasOrgParcelTypes ? (
            <div className="text-center py-8">
              <Package className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-medium mb-2">No Custom Parcel Types</h3>
              <p className="text-muted-foreground mb-4">
                Your organization is using the platform default parcel types. You can copy them to create custom versions or create new ones from scratch.
              </p>
              <div className="flex justify-center space-x-2">
                <Button
                  onClick={copyDefaultParcelTypes}
                  disabled={copyingDefaults}
                  variant="outline"
                  className="flex items-center space-x-2"
                >
                  <Copy className="w-4 h-4" />
                  <span>{copyingDefaults ? 'Copying...' : 'Copy Default Types'}</span>
                </Button>
                <Button onClick={handleAddClick} className="flex items-center space-x-2">
                  <Plus className="w-4 h-4" />
                  <span>Create New Type</span>
                </Button>
              </div>
            </div>
          ) : (
            <ParcelTypesList
              parcelTypes={orgParcelTypes}
              onDragEnd={handleDragEnd}
              onEdit={handleEdit}
              onDelete={handleDelete}
              onToggleEnabled={handleToggleEnabled}
              reordering={reordering}
            />
          )}
        </CardContent>
      </Card>

      <ParcelTypeForm
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        editingType={editingType}
        onSubmit={handleFormSubmit}
        parcelTypesCount={orgParcelTypes.length}
      />
    </div>
  );
};

export default OrganizationParcelTypesManager;